import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../api/axios';
import './GroupedApplicationsInternalExpert.css';

import ApplicationFullViewModal from './modals/ApplicationFullViewModal';
import SelectExpertsModal from './modals/SelectExpertsModal';
import UserProfileModal from '../components/users/UserProfileModal';
import AssignedExpertsModal from './modals/AssignedExpertsModal';

const tMap = {
  kz: {
    sign: 'Қабылдау',
    profileTitle: 'Қолданушы профилі',
    closeProfile: 'Жабу',
    start: 'Басталуы',
    deadline: 'Дедлайн',
    overdue: 'Мерзімі өтті',
    noDeadline: 'Дедлайн жоқ',
    selectExperts: 'Сыртқы сарапшыларды таңдау',
    viewAssigned: 'Тағайындалған сарапшыларды көру',
    loading: 'Жүктелуде...',
    noGroups: 'Топтар табылмады',
    confirmGroup: 'Сіз барлық ОӘК қабылдайтыныңызға сенімдісіз бе?',
    confirmExperts: 'Сіз осы сарапшылар тобын бекітуге сенімдісіз бе?',
    deleteExpertConfirm: 'Осы сарапшыны өшіруге сенімдісіз бе?',
    alertMustSelectAtLeastOne: 'Кемінде бір сарапшы таңдау керек.',
    alertSavedAsDraft: 'Сыртқы сарапшылар жоба күйінде сақталды!',
    alertErrorOccurred: 'Қате орын алды.',
    alertDeleted: 'Сыртқы сарапшы сәтті өшірілді!',
    alertMessageSent: 'Хабарлама жіберілді!',
    alertConfirmed: 'Сыртқы сарапшылар сәтті бекітілді!',
    expandAll: 'Барлығын ашу',
    collapseAll: 'Барлығын жабу',
    appsCount: 'Өтінімдер',
    filterStatus: 'Күйі (өтініш)',
    all: 'Барлығы',
    searchPlaceholder: 'Атауы бойынша іздеу...',
    fullView: 'Толық көру',
    saveDraft: 'Жоба ретінде сақтау',
    noExpertsFound: 'Сарапшылар табылмады',
    selectedScientists: 'Таңдалған ғалымдар',
    selectedTeachers: 'Таңдалған педагогтар',
    tableHeaders: {
      publication: 'Оқу басылымы',
      grade: 'Сынып',
      category: 'Санат',
      status: 'Күйі',
    },

     confirm: 'Бекіту',
  sendMessage: 'Жіберу',
  },
  ru: {
    sign: 'Принять',
    start: 'Начало',
    profileTitle: 'Профиль пользователя',
    closeProfile: 'Закрыть',
    deadline: 'Дедлайн',
    overdue: 'Просрочено',
    noDeadline: 'Нет дедлайна',
    selectExperts: 'Выбрать внешних экспертов',
    viewAssigned: 'Просмотр назначенных экспертов',
    loading: 'Загрузка...',
    noGroups: 'Группы не найдены',
    confirmGroup: 'Вы уверены, что хотите принять все УМК?',
    confirmExperts: 'Вы уверены, что хотите подтвердить данную группу экспертов?',
    deleteExpertConfirm: 'Вы уверены, что хотите удалить этого эксперта?',
    alertMustSelectAtLeastOne: 'Необходимо выбрать хотя бы одного эксперта.',
    alertSavedAsDraft: 'Внешние эксперты сохранены как черновик!',
    alertErrorOccurred: 'Произошла ошибка.',
    alertDeleted: 'Эксперт успешно удалён!',
    alertMessageSent: 'Сообщение отправлено!',
    alertConfirmed: 'Внешние эксперты успешно подтверждены!',
    expandAll: 'Развернуть все',
    collapseAll: 'Свернуть все',
    appsCount: 'Заявок',
    filterStatus: 'Статус (заявка)',
    all: 'Все',
    searchPlaceholder: 'Поиск по названию...',
    fullView: 'Полный просмотр',
    saveDraft: 'Сохранить как черновик',
    noExpertsFound: 'Эксперты не найдены',
    selectedScientists: 'Выбранные учёные',
    selectedTeachers: 'Выбранные педагоги',
    tableHeaders: {
      publication: 'Учебное издание',
      grade: 'Класс',
      category: 'Категория',
      status: 'Статус',
    },
    confirm: 'Подтвердить',
  sendMessage: 'Отправить',
  },
};

const INTERNAL_ASSIGNED = [
  'Ішкі сарапшы тағайындалды',
  'internal_expert назначен',
];

const INTERNAL_ACCEPTED = [
  'Ішкі сарапшы қабылдады',
  'internal_expert принял',
  'Внутренны эксперт отдела принял',
].map((s) => String(s).toLowerCase().trim().replace(/\s+/g, ''));

const normalize = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, '');

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
};

const getTimeLeft = (deadlineAt, locale, t) => {
  const d = toDate(deadlineAt);
  if (!d) return t.noDeadline;

  const diff = d.getTime() - Date.now();
  if (diff <= 0) return t.overdue;

  const totalMin = Math.floor(diff / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;

  const u = locale === 'ru'
    ? { d: 'д', h: 'ч', m: 'м' }
    : { d: 'күн', h: 'сағ', m: 'мин' };

  if (days > 0) return `${days}${u.d} ${hours}${u.h} ${mins}${u.m}`;
  if (hours > 0) return `${hours}${u.h} ${mins}${u.m}`;
  return `${mins}${u.m}`;
};

const getSeverity = (deadlineAt) => {
  const d = toDate(deadlineAt);
  if (!d) return 'none';
  const mins = Math.floor((d.getTime() - Date.now()) / 60000);
  if (mins <= 60) return 'danger';
  if (mins <= 1440) return 'warn';
  return 'ok';
};

const getGroupDeadline = (group) => {
  const apps = Array.isArray(group?.applications) ? group.applications : [];
  const deadlines = apps
    .map((a) => a?.deadline)
    .filter((d) => d?.deadline_at)
    .sort(
      (a, b) =>
        (toDate(a.deadline_at)?.getTime() || Infinity) -
        (toDate(b.deadline_at)?.getTime() || Infinity)
    );

  return deadlines[0] || null;
};

const getLinks = (res) => ({
  prev: res?.prev_page_url || null,
  next: res?.next_page_url || null,
  first: res?.first_page_url || null,
  last: res?.last_page_url || null,
});

const getGroupSubjectId = (group) =>
  group?.subject_id ||
  group?.subject?.id ||
  group?.applications?.[0]?.subject_id ||
  null;

export default function GroupedApplicationsInternalExpert({ locale = 'kz' }) {
  const t = useMemo(() => tMap[locale] || tMap.kz, [locale]);
  const token = useMemo(() => localStorage.getItem('token'), []);
  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept-Language': locale === 'kz' ? 'kk' : 'ru',
      },
    }),
    [token, locale]
  );

  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState(null);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [expanded, setExpanded] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('gai_expanded_groups') || '{}');
    } catch {
      return {};
    }
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allAppStatuses, setAllAppStatuses] = useState([]);

  const [viewingAppId, setViewingAppId] = useState(null);
  const [selectingGroupId, setSelectingGroupId] = useState(null);
  const [viewingGroupId, setViewingGroupId] = useState(null);

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileDetails, setProfileDetails] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileReviewsLoading, setProfileReviewsLoading] = useState(false);
  const [profileReviewsResp, setProfileReviewsResp] = useState(null);
  const [profileMyReview, setProfileMyReview] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);

  const [assignedExperts, setAssignedExperts] = useState([]);
  const [externalExperts, setExternalExperts] = useState({ scientists: [], teachers: [] });
  const [loadingGroupId, setLoadingGroupId] = useState(null);

  const anyOpen = useMemo(() => groups.some((g) => expanded[g.id]), [groups, expanded]);
  const selectingGroup = useMemo(
    () => groups.find((g) => g.id === selectingGroupId),
    [groups, selectingGroupId]
  );
  const viewingGroup = useMemo(
    () => groups.find((g) => g.id === viewingGroupId),
    [groups, viewingGroupId]
  );

  const getTitle = (a) =>
    a?.title || a?.name || a?.publication_title || a?.publication?.title || '—';
  const getGrade = (a, g) =>
    a?.grade || a?.grade_name || a?.class || a?.grade?.name || g?.grade || '—';
  const getCategory = (a, g) =>
    a?.category || a?.category_name || a?.subject || a?.category?.name || g?.subject || '—';
  const getStatus = (a) => a?.status || a?.application_status || a?.state || '—';

  const fetchGroups = useCallback(
    async (url = '/expertise/internal/groups') => {
      setLoading(true);
      setErr('');

      try {
        const { data: res } = await axios.get(url, authHeaders);
        const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

        setGroups(arr);
        setMeta({
          current_page: res?.current_page || 1,
          last_page: res?.last_page || 1,
        });
        setLinks(getLinks(res));

        const statuses = new Set();
        arr.forEach((g) =>
          (g?.applications || []).forEach((a) => {
            if (a?.status) statuses.add(String(a.status));
          })
        );
        setAllAppStatuses([...statuses].sort());
      } catch (e) {
        console.error(e);
        setGroups([]);
        setMeta(null);
        setLinks(null);
        setAllAppStatuses([]);
        setErr(t.alertErrorOccurred);
      } finally {
        setLoading(false);
      }
    },
    [authHeaders, t.alertErrorOccurred]
  );

  const fetchExternalExperts = useCallback(
    async (subjectId = null) => {
      try {
        const { data } = await axios.get('/expertise/internal/external-experts', {
          ...authHeaders,
          params: subjectId ? { subject_id: subjectId } : {},
        });

        setExternalExperts({
          scientists: data?.scientists || [],
          teachers: data?.teachers || [],
        });
      } catch (e) {
        console.error('External experts loading error', e);
        setExternalExperts({ scientists: [], teachers: [] });
      }
    },
    [authHeaders]
  );

  const fetchAssignedExperts = useCallback(
    async (groupId) => {
      try {
        const { data } = await axios.get(
          `/expertise/internal/groups/${groupId}/assigned-experts`,
          authHeaders
        );
        setAssignedExperts(data?.experts || []);
      } catch (e) {
        console.error(e);
        setAssignedExperts([]);
      }
    },
    [authHeaders]
  );

  const loadProfileReviews = useCallback(
    async (user, pageNum = 1) => {
      const userId = user?.user_id ?? user?.id;
      if (!userId) return;

      setProfileReviewsLoading(true);
      try {
        const res = await axios.get(`/backoffice/users/${userId}/reviews`, {
          ...authHeaders,
          params: { page: pageNum },
        });
        const payload = res?.data?.data || res?.data || null;
        setProfileReviewsResp(payload);
        setProfileMyReview(payload?.my_review || null);
      } catch (e) {
        console.error(e);
        setProfileReviewsResp(null);
        setProfileMyReview(null);
      } finally {
        setProfileReviewsLoading(false);
      }
    },
    [authHeaders]
  );

  const openProfile = useCallback(
    async (userOrId) => {
      const user =
        typeof userOrId === 'object' && userOrId !== null
          ? userOrId
          : { id: userOrId };

      const userId = user?.user_id ?? user?.id;
      if (!userId) return;

      setProfileUser(user);
      setProfileOpen(true);
      setProfileLoading(true);
      setProfileDetails(null);
      setProfileReviewsResp(null);
      setProfileMyReview(null);

      const [userRes, reviewsRes] = await Promise.allSettled([
        axios.get(`/backoffice/users/${userId}`, authHeaders),
        axios.get(`/backoffice/users/${userId}/reviews`, authHeaders),
      ]);

      if (userRes.status === 'fulfilled') {
        const payload = userRes.value?.data?.data || userRes.value?.data || null;
        setProfileDetails(payload);
      }

      if (reviewsRes.status === 'fulfilled') {
        const payload = reviewsRes.value?.data?.data || reviewsRes.value?.data || null;
        setProfileReviewsResp(payload);
        setProfileMyReview(payload?.my_review || null);
      }

      setProfileLoading(false);
    },
    [authHeaders]
  );

  const closeProfile = useCallback(() => {
    setProfileOpen(false);
    setProfileUser(null);
    setProfileDetails(null);
    setProfileLoading(false);
    setProfileReviewsLoading(false);
    setProfileReviewsResp(null);
    setProfileMyReview(null);
  }, []);

  const handleAcceptGroup = useCallback(
    async (groupId) => {
      if (!window.confirm(t.confirmGroup)) return;

      setLoadingGroupId(groupId);
      try {
        await axios.post(`/expertise/internal/groups/${groupId}/accept`, {}, authHeaders);
        await fetchGroups();
        setExpanded((p) => ({ ...p, [groupId]: true }));
      } catch (e) {
        console.error(e);
        alert(t.alertErrorOccurred);
      } finally {
        setLoadingGroupId(null);
      }
    },
    [authHeaders, fetchGroups, t.confirmGroup, t.alertErrorOccurred]
  );

const handleSaveDraftExperts = useCallback(
  async (groupId, payload) => {
    setDraftLoading(true);
    try {
      await axios.post(
        `/expertise/internal/groups/${groupId}/draft-expert`,
        payload,
        authHeaders
      );
      alert(t.alertSavedAsDraft);
      setSelectingGroupId(null);
      await fetchGroups();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || t.alertErrorOccurred);
    } finally {
      setDraftLoading(false);
    }
  },
  [authHeaders, fetchGroups, t.alertSavedAsDraft, t.alertErrorOccurred]
);

  const handleSendMessage = useCallback(
    async (groupId, expertId) => {
      if (
        !window.confirm(
          locale === 'kz'
            ? 'Осы сарапшыға хабарлама жібергіңіз келе ме?'
            : 'Вы хотите отправить сообщение этому эксперту?'
        )
      ) {
        return;
      }

      try {
        await axios.post(
          `/expertise/internal/groups/${groupId}/send-expert`,
          { expert_id: expertId },
          authHeaders
        );
        alert(t.alertMessageSent);
        await fetchAssignedExperts(groupId);
      } catch (e) {
        console.error(e);
        alert(t.alertErrorOccurred);
      }
    },
    [authHeaders, fetchAssignedExperts, locale, t.alertMessageSent, t.alertErrorOccurred]
  );

  const handleDeleteExpert = useCallback(
    async (groupId, expertId) => {
      if (!window.confirm(t.deleteExpertConfirm)) return;

      try {
        await axios.delete(`/expertise/internal/groups/${groupId}/delete-expert`, {
          ...authHeaders,
          data: { expert_id: expertId },
        });
        alert(t.alertDeleted);
        await fetchAssignedExperts(groupId);
      } catch (e) {
        console.error(e);
        alert(t.alertErrorOccurred);
      }
    },
    [authHeaders, fetchAssignedExperts, t.deleteExpertConfirm, t.alertDeleted, t.alertErrorOccurred]
  );

  const handleConfirmExperts = useCallback(
    async (groupId) => {
      if (!window.confirm(t.confirmExperts)) return;

      try {
        await axios.post(`/expertise/internal/groups/${groupId}/confirm-experts`, {}, authHeaders);
        alert(t.alertConfirmed);
        setViewingGroupId(null);
        await fetchGroups();
      } catch (e) {
        console.error(e);
        alert(e?.response?.data?.message || t.alertErrorOccurred);
      }
    },
    [authHeaders, fetchGroups, t.confirmExperts, t.alertConfirmed, t.alertErrorOccurred]
  );

  const gotoPage = useCallback(
    async (kind) => {
      const url = links?.[kind];
      if (url) await fetchGroups(url);
    },
    [links, fetchGroups]
  );

  const toggleGroup = useCallback((id) => {
    setExpanded((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('gai_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, []);

  const toggleAllGroups = useCallback(() => {
    const target = !anyOpen;
    setExpanded((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        next[g.id] = target;
      });
      localStorage.setItem('gai_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, [anyOpen, groups]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = {};
      groups.forEach((g) => {
        next[g.id] = prev[g.id] ?? false;
      });
      localStorage.setItem('gai_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, [groups]);

  useEffect(() => {
    const hasModal = !!(selectingGroupId || viewingGroupId || profileOpen || viewingAppId);
    document.body.classList.toggle('gai-no-scroll', hasModal);
    return () => document.body.classList.remove('gai-no-scroll');
  }, [selectingGroupId, viewingGroupId, profileOpen, viewingAppId]);

  useEffect(() => {
    const id = setInterval(() => setGroups((x) => [...x]), 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="gai-root">
      <div className="gai-search" style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="gai-input"
          placeholder={t.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="gai-btn" onClick={() => setSearchQuery('')}>
            ×
          </button>
        )}
      </div>

      <div className="gai-toolbar">
        <button className="gai-btn" onClick={toggleAllGroups}>
          {anyOpen ? t.collapseAll : t.expandAll}
        </button>

        <div className="gai-filter">
          <label className="gai-filter-label">
            {t.filterStatus}:
            <select
              className="gai-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{t.all}</option>
              {allAppStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading && groups.length === 0 && <p className="gai-muted">{t.loading}</p>}
      {!loading && !groups.length && !err && <p className="gai-muted">{t.noGroups}</p>}
      {err && <p className="gai-error">{err}</p>}

      {groups.map((group) => {
        const isOpen = !!expanded[group.id];
        const apps = Array.isArray(group?.applications) ? group.applications : [];

        const filteredApps = apps.filter((a) => {
          const byStatus = statusFilter === 'all' || String(getStatus(a)) === statusFilter;
          const byTitle =
            !searchQuery ||
            String(getTitle(a)).toLowerCase().includes(searchQuery.toLowerCase());
          return byStatus && byTitle;
        });

        if (!filteredApps.length) return null;

        const showSign = filteredApps.every((a) => INTERNAL_ASSIGNED.includes(getStatus(a)));
        const showActions = filteredApps.every((a) =>
          INTERNAL_ACCEPTED.includes(normalize(getStatus(a)))
        );
        const deadline = getGroupDeadline(group);

        return (
          <div
            key={group.id}
            className={`gai-group-section ${isOpen ? 'is-open' : 'is-closed'}`}
          >
            <div
              className="gai-head"
              role="button"
              tabIndex={0}
              onClick={() => toggleGroup(group.id)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && toggleGroup(group.id)
              }
            >
              <span className="gai-chev">{isOpen ? '▾' : '▸'}</span>

              <span className="gai-group-title">
                {group.title || '—'}
                {group.publisher_name && (
                  <span className="gai-group-publisher"> — {group.publisher_name}</span>
                )}
              </span>

              <span className="gai-group-count">
                {t.appsCount}: {filteredApps.length}
              </span>

              <span
                className={`gai-deadline ${getSeverity(deadline?.deadline_at)} ${
                  deadline?.is_overdue ? 'overdue' : ''
                }`}
                title={
                  deadline
                    ? `${t.start}: ${deadline.start_at || '—'}\n${t.deadline}: ${
                        deadline.deadline_at || '—'
                      }`
                    : t.noDeadline
                }
                style={{ marginLeft: 8 }}
              >
                {getTimeLeft(deadline?.deadline_at, locale, t)}
              </span>

              <div className="gai-head__spacer" />

              {showSign && (
                <button
                  className="gai-btn-approve-group"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcceptGroup(group.id);
                  }}
                  disabled={loadingGroupId === group.id}
                >
                  {loadingGroupId === group.id ? t.loading : t.sign}
                </button>
              )}

              {showActions && (
                <div className="gai-row-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="gai-btn-primary"
                    onClick={async () => {
                      const serviceTypeId = Number(group?.service_type_id || 1);

                      if (serviceTypeId === 2) {
                        setExternalExperts({ scientists: [], teachers: [] });
                        setSelectingGroupId(group.id);
                        return;
                      }

                      await fetchExternalExperts(getGroupSubjectId(group));
                      setSelectingGroupId(group.id);
                    }}
                  >
                    {t.selectExperts}
                  </button>

                  <button
                    className="gai-btn-secondary"
                    onClick={async () => {
                      await fetchAssignedExperts(group.id);
                      setViewingGroupId(group.id);
                    }}
                  >
                    {t.viewAssigned}
                  </button>
                </div>
              )}
            </div>

            <div id={`gai-panel-${group.id}`} className="gai-group-panel" hidden={!isOpen}>
              <table className="gai-table">
                <thead>
                  <tr>
                    <th>{t.tableHeaders.grade}</th>
                    <th>{t.tableHeaders.category}</th>
                    <th>{t.tableHeaders.status}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((a) => (
                    <tr key={`${group.id}-${a.id}`}>
                      <td>{getGrade(a, group)}</td>
                      <td>{getCategory(a, group)}</td>
                      <td>{getStatus(a)}</td>
                      <td>
                        <button
                          className="gai-btn-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingAppId(a.id);
                          }}
                        >
                          {t.fullView}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {meta?.last_page > 1 && (
        <div className="gai-pager-wrap">
          <div className="gai-pager">
            <button disabled={!links?.prev} onClick={() => gotoPage('prev')}>
              «
            </button>
            <span>
              {meta.current_page} / {meta.last_page}
            </span>
            <button disabled={!links?.next} onClick={() => gotoPage('next')}>
              »
            </button>
          </div>
        </div>
      )}

      {selectingGroupId && !profileOpen && (
  <SelectExpertsModal
    t={t}
    locale={locale}
    serviceTypeId={selectingGroup?.service_type_id || 1}
    scientists={externalExperts.scientists}
    teachers={externalExperts.teachers}
    maxScientists={externalExperts.scientists?.length || 50}
    maxTeachers={externalExperts.teachers?.length || 50}
    onViewExpert={openProfile}
    onSaveDraft={(payload) => handleSaveDraftExperts(selectingGroupId, payload)}
    draftLoading={draftLoading}   // ← добавить
    onClose={() => setSelectingGroupId(null)}
  />
)}

      {profileOpen
        ? createPortal(
            <div className="upm-overlay" onClick={closeProfile}>
              <div
                className="upm-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={t.profileTitle}
              >
                <button
                  type="button"
                  className="upm-close"
                  onClick={closeProfile}
                  aria-label={t.closeProfile}
                >
                  ×
                </button>

                {profileLoading ? (
                  <div className="upm-loading">{t.loading}</div>
                ) : (
                  <UserProfileModal
                    user={profileUser}
                    userDetails={profileDetails}
                    locale={locale}
                    reviewsLoading={profileReviewsLoading}
                    reviewsResp={profileReviewsResp}
                    myReview={profileMyReview}
                    canWriteReview={true}
                    canManageReview={false}
                    onReviewsTabOpen={() => loadProfileReviews(profileUser, 1)}
                    onReviewsPageChange={(p) => loadProfileReviews(profileUser, p)}
                  />
                )}
              </div>
            </div>,
            document.body
          )
        : null}

      {viewingGroupId && (
        <AssignedExpertsModal
          t={t}
          locale={locale}
          educationLevelId={
            viewingGroup?.education_level?.name_kz ||
            viewingGroup?.education_level?.name_ru ||
            viewingGroup?.education_level?.title ||
            viewingGroup?.education_level ||
            ''
          }
          experts={assignedExperts}
          onSendMessage={(expertId) => handleSendMessage(viewingGroupId, expertId)}
          onDeleteExpert={(expertId) => handleDeleteExpert(viewingGroupId, expertId)}
          onConfirm={() => handleConfirmExperts(viewingGroupId)}
          onClose={() => setViewingGroupId(null)}
        />
      )}

      {viewingAppId && (
        <ApplicationFullViewModal
          appId={viewingAppId}
          locale={locale}
          onClose={() => setViewingAppId(null)}
        />
      )}
    </div>
  );
}